import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { AnyEntity, EntityType, EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { isMapped } from "@mapmycustomers/shared/util/assert";
import { convertGeoPointToLatLngLiteral } from "@mapmycustomers/shared/util/geo/GeoService";
import { Button } from "@mapmycustomers/ui";

import { useMap } from "@app/component/map/FeaturedMap";
import GeoLocationInfo from "@app/component/preview/components/GeoLocationInfo";
import { enterPinLocationMode, exitPinLocationMode } from "@app/scene/map/store/actions";
import { getMapViewTool, isPinLocationActive } from "@app/scene/map/store/selectors";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { subscribe, unsubscribe } from "@app/store/uiSync/actions";

interface OwnProps {
  entity: EntityWithLocation;
  onUpdate?: (updatedEntity: EntityWithLocation) => void;
}

interface Props extends OwnProps {
  isActive: boolean;
  isPinLocationMode: boolean;
  isReadOnlyMember: boolean;
  onEnterPinLocationMode: typeof enterPinLocationMode;
  onExitPinLocationMode: typeof exitPinLocationMode;
  subscribe: typeof subscribe;
  unsubscribe: typeof unsubscribe;
}

const PinLocationToolButton: React.FC<Props> = ({
  entity,
  isActive,
  isPinLocationMode,
  isReadOnlyMember,
  onEnterPinLocationMode,
  onExitPinLocationMode,
  onUpdate,
  subscribe,
  unsubscribe,
}) => {
  const intl = useIntl();
  const { map } = useMap();
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const hasLocation = isMapped(entity);

  useEffect(() => {
    return () => {
      if (isPinLocationMode) {
        onExitPinLocationMode();
      }
    };
  }, [isPinLocationMode, onExitPinLocationMode]);

  useEffect(() => {
    if (hasLocation && entity?.entity) {
      const listener = {
        onUpdate: (entityType: EntityType, entities: AnyEntity[]) => {
          const updatedEntity = entities.find((record) => record.id === entity.id);

          if (updatedEntity) {
            onUpdate?.(updatedEntity as EntityWithLocation);
          }
        },
      };

      subscribe({ entityTypes: [entity.entity], listener });
      return () => {
        unsubscribe({ entityTypes: [entity.entity], listener });
      };
    }
  }, [entity, hasLocation, onUpdate, subscribe, unsubscribe]);

  const handleClick = useCallback(() => {
    setTooltipVisible(false);
    if (hasLocation) {
      map.setCenter(convertGeoPointToLatLngLiteral(entity.geoPoint!));
      onEnterPinLocationMode({ entity });
    } else {
      const center = map.getCenter();
      onEnterPinLocationMode({
        entity:
          center?.lng() && center?.lat()
            ? {
                ...entity,
                geoPoint: { coordinates: [center.lng(), center.lat()], type: "Point" },
              }
            : entity,
        entityWithoutLocation: true,
      });
    }

    if (map.getZoom()! < 15) {
      map.setZoom(20);
    }
  }, [entity, hasLocation, map, onEnterPinLocationMode, setTooltipVisible]);

  return (
    <>
      {isActive ? (
        <div>
          {intl.formatMessage({
            id: "recordPane.pinLocationMode.notice",
            defaultMessage: "Use the map on the left to reposition the pin",
            description: "Notice for change pin location button on record pane (map mode)",
          })}
        </div>
      ) : (
        <GeoLocationInfo entity={entity} />
      )}
      <Tooltip
        destroyTooltipOnHide
        onOpenChange={setTooltipVisible}
        open={tooltipVisible}
        title={intl.formatMessage({
          id: "recordPane.pinLocationMode.tooltip",
          defaultMessage: "Change record pin location by dragging it's pin on the map",
          description: "Tooptip for change pin location button on record pane (map mode)",
        })}
        trigger={isActive || !hasLocation ? [] : ["hover"]}
      >
        <Button disabled={isActive || isReadOnlyMember} onClick={handleClick}>
          {hasLocation
            ? intl.formatMessage({
                id: "recordPane.pinLocationMode.changeButton",
                defaultMessage: "Change pin location",
                description: "Label for change pin location button on record pane (map mode)",
              })
            : intl.formatMessage({
                id: "recordPane.pinLocationMode.changeButton.setOnMap",
                defaultMessage: "Set on Map",
                description:
                  "Label for change pin location button on record pane (map mode) - set on map",
              })}
        </Button>
      </Tooltip>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isActive: isPinLocationActive(state),
  isPinLocationMode: getMapViewTool(state) === MapTool.PIN_LOCATION,
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

const mapDispatchToProps = {
  onEnterPinLocationMode: enterPinLocationMode,
  onExitPinLocationMode: exitPinLocationMode,
  subscribe,
  unsubscribe,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinLocationToolButton);
